/**
 * Dispute Agent types — aligned with the 6-state BE model.
 *
 * Backend: django-fsm-2 on Invoice model
 * States: none → active ↔ awaiting-vendor → resolved / withdrawn
 *
 * PRD: https://linear.app/liftai/document/prd-dispute-management-agent-dd09a381716d
 */

// ── Dispute Status (BE 6-state model) ───────────────────────────────────

export type DisputeStatus = 'none' | 'active' | 'awaiting-vendor' | 'resolved' | 'withdrawn';

export const DISPUTE_STATUS_LABEL: Record<DisputeStatus, string> = {
  none: 'No Dispute',
  active: 'Active',
  'awaiting-vendor': 'Awaiting Vendor',
  resolved: 'Resolved',
  withdrawn: 'Withdrawn',
};

export const DISPUTE_STATUS_COLOR: Record<
  DisputeStatus,
  'default' | 'info' | 'warning' | 'success' | 'error' | 'primary'
> = {
  none: 'default',
  active: 'primary',
  'awaiting-vendor': 'info',
  resolved: 'success',
  withdrawn: 'default',
};

/**
 * Whether a value off the API is a status this build knows how to render.
 * `Invoice.disputeStatus` is typed as a plain string, so every surface indexing one of
 * the maps above needs a way to narrow it that isn't an assertion.
 */
export const isDisputeStatus = (value: string | null | undefined): value is DisputeStatus =>
  value != null && value in DISPUTE_STATUS_LABEL;

/**
 * Returns the display label for a dispute status, accounting for "Action Needed" state.
 * When disputeNeedsAttention is true and status is 'active', returns "Action Needed".
 */
export const getDisputeStatusLabel = (status: DisputeStatus, needsAttention?: boolean): string => {
  if (needsAttention && status === 'active') {
    return 'Action Needed';
  }
  return DISPUTE_STATUS_LABEL[status];
};

// ── Vendor Response Categories ──────────────────────────────────────────

export type VendorPosition =
  | 'agreed'
  | 'partial-agreement'
  | 'disagreed-with-evidence'
  | 'disagreed-without-evidence'
  | 'requested-more-info';

export const VENDOR_POSITION_CONFIG: Record<
  VendorPosition,
  { label: string; description: string; color: string }
> = {
  agreed: {
    label: 'Vendor Agreed',
    description: 'Vendor accepted and will issue credit/void',
    color: 'success.main',
  },
  'partial-agreement': {
    label: 'Partial Agreement',
    description: 'Vendor offers partial credit or adjustment',
    color: 'warning.main',
  },
  'disagreed-with-evidence': {
    label: 'Disagreed (with evidence)',
    description: 'Vendor rejects but provided documentation',
    color: 'error.main',
  },
  'disagreed-without-evidence': {
    label: 'Disagreed (no evidence)',
    description: 'Vendor rejects without providing proof',
    color: 'error.light',
  },
  'requested-more-info': {
    label: 'Requested Information',
    description: 'Vendor needs more details before responding',
    color: 'info.main',
  },
};

// ── Resolution Types ────────────────────────────────────────────────────

export type ResolutionType =
  | 'credit-issued'
  | 'adjusted-invoice-received'
  | 'vendor-rejected'
  | 'withdrawn'
  | 'other';

export const RESOLUTION_TYPE_LABEL: Record<ResolutionType, string> = {
  'credit-issued': 'Credit Issued',
  'adjusted-invoice-received': 'Adjusted Invoice Received',
  'vendor-rejected': 'Vendor Rejected',
  withdrawn: 'Withdrawn',
  other: 'Other',
};

// ── Timeline Entry (from GET /dispute/timeline/) ────────────────────────

// Delivery outcome for an outbound dispute email, driven by SendGrid Event
// Webhook events. Mirrors DisputeTimelineEntry.DeliveryStatus on the BE.
export type DeliveryStatus =
  | 'sent'
  | 'delivered'
  | 'deferred'
  | 'bounced'
  | 'dropped'
  | 'spam-reported';

// Per-status presentation for the timeline. `isFailure` marks the terminal
// outcomes where the vendor did NOT receive the email — those get a distinct
// chip + reason line. 'sent' (accepted, no event yet), 'delivered', and
// 'deferred' (transient, still retrying) are not failures and render no extra
// chip, so a normal send stays visually quiet. Labels mirror the BE
// DisputeTimelineEntry.DeliveryStatus choices.
export const DELIVERY_STATUS_CONFIG: Record<DeliveryStatus, { label: string; isFailure: boolean }> =
  {
    sent: { label: 'Sent', isFailure: false },
    delivered: { label: 'Delivered', isFailure: false },
    deferred: { label: 'Delivery deferred', isFailure: false },
    bounced: { label: 'Bounced', isFailure: true },
    dropped: { label: 'Dropped', isFailure: true },
    'spam-reported': { label: 'Reported as spam', isFailure: true },
  };

export type TimelineEntryType =
  | 'dispute-email-sent'
  | 'data-request-sent'
  | 'follow-up-sent'
  | 'vendor-response'
  // A reply that isn't a genuine vendor response — an out-of-office/auto-reply
  // or a consultant's own reply landing in the thread. Kept distinct so the
  // timeline doesn't mislabel it as a real "Vendor Response".
  | 'non-vendor-reply'
  | 'resolution'
  | 'adjusted-invoice-linked'
  | 'adjusted-invoice-approved'
  | 'adjusted-invoice-rejected';

// Delivery status for an outbound entry. Inbound/system events are always
// 'sent'. An outbound email is 'pending' while in flight, then 'sent' once the
// platform accepts it or 'failed' if the send failed — so the timeline never
// claims an email reached the vendor when it didn't (AMP-541).
type TimelineEntryStatus = 'pending' | 'sent' | 'failed';

export interface TimelineEntry {
  id: string;
  type: TimelineEntryType;
  status: TimelineEntryStatus;
  timestamp: string;
  // Nullable: non-email entries (status/system events) have no direction. The
  // timeline only highlights inbound entries, so null renders neutrally.
  direction: 'inbound' | 'outbound' | null;
  subject: string;
  body: string;
  sender: string;
  recipients: string[];
  cc: string[];
  sentBy: string | null;
  vendorPosition: string | null;
  vendorResponseSummary: string | null;
  emailPlatformId: string | null;
  emailPlatformThreadId: string | null;
  // Delivery outcome from the SendGrid Event Webhook. Null for inbound entries
  // or before any delivery event has arrived.
  deliveryStatus: DeliveryStatus | null;
  // Reason from the delivery event (e.g. bounce reason); empty when none.
  deliveryStatusDetail: string;
  attachments: Attachment[];
  // Why the send failed; populated only when status is 'failed'.
  failureReason: string;
  // Intended evidence files that could not be loaded and did not go out.
  failedAttachments: string[];
}

// ── Email Draft (from BE) ───────────────────────────────────────────────

type DraftType = 'dispute' | 'data_request' | 'follow_up';

/** Attachment as returned from BE (timeline entries, draft) */
export interface Attachment {
  id: string;
  filename: string;
  fileType: 'pdf' | 'image';
  // Null when the underlying Attachment record has no file
  // (`att.file.url if att.file else None` on the BE, dispute/serializers.py).
  url: string | null;
}

export interface EmailDraft {
  // Optional: `Invoice.dispute_current_draft` is a raw JSONField the BE persists
  // and echoes back verbatim, seeded as just `{from_email}` before the LLM call
  // that fills in the rest ever runs — so a draft can be observed with only
  // `fromEmail` set (see the schema override in useDisputeAgent.ts, AMP-593).
  draftType?: DraftType;
  fromEmail?: string; // Read-only, configured in Email Integration Platform
  subject?: string;
  body?: string;
  to?: string[];
  cc?: string[];
  isAgentGenerated?: boolean;
  basedOnFindings?: boolean;
  attachments?: Attachment[];
  attachmentIds?: string[]; // For updating draft - array of existing Attachment UUIDs
}

// ── Agent Suggestion (from BE compute_suggestions) ──────────────────────

export interface AgentSuggestion {
  action: string;
  label: string;
  reason: string;
  priority: 'primary' | 'secondary' | 'overflow';
  payload?: Record<string, unknown> | null;
}

// ── Available Action (from GET /dispute/actions/) ───────────────────────

export interface AvailableAction {
  action: string;
  label: string;
  priority: 'primary' | 'secondary' | 'overflow';
}

// ── Resolution (nested in DisputeAgentState) ────────────────────────────

interface DisputeResolution {
  resolutionType: string;
  settledAmount: string;
  savings: string;
  notes: string;
}

// ── Main Agent State (from GET /dispute/) ───────────────────────────────

export interface DisputeAgentState {
  invoiceId: string;
  status: DisputeStatus;
  initiatedAt: string | null;
  vendorPosition: string | null;
  followUpCount: number;
  maxFollowUps: number;
  nextFollowUpDate: string | null;
  daysSinceLastContact: number;
  resolution: DisputeResolution | null;
  suggestedActions: AgentSuggestion[];
  attentionReason: string;
  currentDraft: EmailDraft | null;
}
